// Internal PDF links from the pallets table to each pallet's detail pages.
// react-pdf turns `<View id="x">` into a named destination and `<Link src="#x">` into a jump to it.

export const palletAnchorId = (layerId: string) => `pallet-${layerId}`;

export interface PalletAnchors {
    // Returns the destination id for the first detail page of a pallet, undefined for any later page.
    claim: (unitLayer?: { id?: string }) => string | undefined;
    // Returns the destination to link a pallets table row to, undefined when the pallet has no detail page.
    linkTarget: (layerId?: string) => string | undefined;
    claimed: Set<string>;
}

export const createPalletAnchors = (
    enabled: boolean,
    reportLayerId: string | undefined,
    linkedLayerIds: Set<string> = new Set()
): PalletAnchors => {
    const claimed = new Set<string>();
    return {
        claimed,
        claim: (unitLayer) => {
            const id = unitLayer?.id;
            // The report layer (e.g. the order overview) is not a pallet page.
            if (!enabled || !id || id === reportLayerId || claimed.has(id)) return undefined;
            claimed.add(id);
            return palletAnchorId(id);
        },
        linkTarget: (layerId) => (enabled && layerId && linkedLayerIds.has(layerId) ? palletAnchorId(layerId) : undefined),
    };
};

// The pallets table is rendered before the pallets' detail pages, so the pages are rendered once to learn
// which pallets got a destination, then again with the table linking to exactly those.
export const renderWithPalletAnchors = <T>(
    enabled: boolean,
    reportLayerId: string | undefined,
    render: (anchors: PalletAnchors) => T
): T => {
    if (!enabled) return render(createPalletAnchors(false, reportLayerId));
    const firstPass = createPalletAnchors(true, reportLayerId);
    render(firstPass);
    return render(createPalletAnchors(true, reportLayerId, firstPass.claimed));
};
